import { LogIn, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { copy } from '../../shared/i18n';
import { useAuth, type AuthContextValue } from './AuthProvider';

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  return <RequireAuthView auth={auth}>{children}</RequireAuthView>;
}

export function RequireAuthView({ auth, children }: { auth: AuthContextValue; children: ReactNode }) {
  if (!auth.configured) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f8faf6] p-4">
        <Card className="w-full max-w-md overflow-hidden">
          <CardHeader>
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-[#f3e5df] text-[#cf7766]">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" />
            </div>
            <CardTitle>{copy.auth.setupRequired}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[#5f6b58]">
            <p>{copy.auth.setupBody}</p>
            <p className="rounded-md bg-[#eef3ea] px-3 py-2 font-mono text-xs text-[#4f5d49]">
              VITE_COGNITO_USER_POOL_CLIENT_ID
              <br />
              VITE_COGNITO_DOMAIN
            </p>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!auth.signedIn) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f8faf6] px-4 py-8">
        <Card className="w-full max-w-md overflow-hidden">
          <CardHeader className="bg-[#eef3ea] px-6 py-5">
            <div className="flex items-center gap-3">
              <img src="/happy-bark-logo.webp" alt="Happy Bark" className="h-12 w-12 rounded-full object-cover ring-1 ring-[#d9dfd1]" />
              <div>
                <p className="text-sm font-semibold text-[#223126]">Happy Bark</p>
                <p className="text-xs text-[#687461]">{copy.app.staffConsole}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5 p-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#cf7766]">Panel del equipo</p>
              <CardTitle className="mt-2 text-2xl text-[#223126]">Bienvenido a Happy Bark</CardTitle>
              <p className="mt-2 text-sm leading-6 text-[#5f6b58]">Accede al panel para gestionar reservas, clientes, perros y pagos.</p>
            </div>
            <Button className="h-10 w-full" onClick={() => void auth.signIn()}>
              <LogIn className="h-4 w-4" aria-hidden="true" />
              {copy.auth.signIn}
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return children;
}
